import { ApiError, PaginatedResponse } from '@gorila-shared-ui/components';
import { ApiEndpoint } from '../types/api';
import { Notification, NotificationCore, NotificationTypeItem } from '../types/notification';
import { getRequest, postRequest } from './api';
import {
  allNotificationTypesUrl,
  batchNotificationsUrl,
  historicNotificationUrl,
  notificationUrl,
  notificationsUrls,
} from './urls/notificationUrls';

export const getNotifications = async (
  clientId: string,
  subClientId: string,
  notificationType?: NotificationTypeItem,
  after?: string,
): Promise<{
  notifications?: NotificationCore[];
  cursor: {
    after: string;
    before: string;
    hasNext: boolean;
  };
  totalUnread?: number;
  error?: ApiError;
}> => {
  const notificationTypeData =
    notificationType && notificationType.notificationId >= 0
      ? {
          notificationType: notificationType.notificationId,
          origin: notificationType.origin,
        }
      : {};
  let queryParams = {
    clientId,
    subClientId,
    after: after || undefined,
    ...notificationTypeData,
  };
  const response = await getRequest(notificationsUrls, {
    queryParams,
  });

  return {
    notifications: response?.data?.items,
    totalUnread: response?.data?.totalUnread,
    cursor: {
      after: response?.data?.after,
      before: response?.data?.before,
      hasNext: response?.data?.hasNext,
    },
    error: response?.error,
  };
};

export const getNotification = async (
  clientId: string,
  subClientId: string,
  notificationId: string,
): Promise<{
  notification?: Notification;
  error?: ApiError;
}> => {
  const response = await getRequest(notificationUrl(notificationId), {
    queryParams: {
      clientId,
      subClientId,
    },
  });

  return {
    notification: response?.data,
    error: response?.error,
  };
};

export const getHistoricNotification = async (
  clientId: string,
  subClientId: string,
  assetId: string,
  page: number,
  notificationType?: NotificationTypeItem,
): Promise<{
  paginatedNotification?: PaginatedResponse<Notification>;
  error?: ApiError;
}> => {
  const notificationTypeData =
    notificationType && notificationType.notificationId >= 0
      ? {
          notificationType: notificationType.notificationId,
          origin: notificationType.origin,
        }
      : {};
  const response = await getRequest(historicNotificationUrl(assetId), {
    queryParams: {
      clientId,
      subClientId,
      page,
      endDate: Date.now(),
      ...notificationTypeData,
    },
  });

  return {
    paginatedNotification: response?.data,
    error: response?.error,
  };
};

export const markNotification = async (
  clientId: string,
  subClientId: string,
  notificationId: string,
  action: string,
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(notificationUrl(notificationId), {
    body: {
      clientId,
      subClientId,
      action,
    },
  });
  return {
    error: response?.error,
  };
};

export const markAllNotifications = async (
  clientId: string,
  subClientId: string,
  action: string,
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(batchNotificationsUrl, {
    body: {
      clientId,
      subClientId,
      action,
    },
  });
  return {
    error: response?.error,
  };
};

export const getNotificationTypes = async (): Promise<{
  notificationTypes: NotificationTypeItem[];
  error?: ApiError;
}> => {
  const response = await getRequest(allNotificationTypesUrl, undefined, undefined, ApiEndpoint.core);
  return {
    notificationTypes: response?.data,
    error: response?.error,
  };
};
